// BRIEF 108 §1 — the WhatsApp rate form, client side.
//
// The keys and the defaults are NOT written here: they come from
// `shared/whatsappRates.json`, the one file the server calculator reads too
// (server/src/lib/whatsappBilling.js). A firm looking at an empty field in
// Settings is looking at the number its invoices were actually computed from,
// because there is only one number.
//
// The form holds STRINGS, not numbers, because a controlled `<input
// type="number">` does — a user mid-edit has typed "1" on the way to "15", and
// coercing on every keystroke deletes the character they are about to type.
// Coercion happens once, at save, in `whatsappRatePayload`.

import rateTable from "@shared/whatsappRates.json";

export type WhatsappRateField =
  | "freeWords"
  | "minimumMinutes"
  | "readingMinutes"
  | "readingBlockSize"
  | "writingMinutes"
  | "writingBlockSize";

/** Rendering order, and it is the order the brief's rate table states. */
export const WHATSAPP_RATE_FIELDS: readonly WhatsappRateField[] = [
  "freeWords",
  "minimumMinutes",
  "readingMinutes",
  "readingBlockSize",
  "writingMinutes",
  "writingBlockSize",
];

export const WHATSAPP_RATE_KEYS: Readonly<Record<WhatsappRateField, string>> =
  rateTable.keys;

export const WHATSAPP_RATE_DEFAULTS: Readonly<Record<WhatsappRateField, number>> =
  rateTable.defaults;

/** The two fields whose value is a WORD COUNT rather than a duration. */
export const WHATSAPP_BLOCK_SIZE_FIELDS: readonly WhatsappRateField[] = [
  "readingBlockSize",
  "writingBlockSize",
];

export type WhatsappRateForm = Record<WhatsappRateField, string>;

// §4 — "The toggle's default side is a Setting." Not a rate, so it is not one
// of the six; the same file carries it because the route and this screen both
// need the key name and the two legal values.
export type WhatsappDestination = "case" | "client";
export const WHATSAPP_DESTINATION_KEY: string = rateTable.destination.key;
export const WHATSAPP_DESTINATIONS: readonly WhatsappDestination[] =
  rateTable.destination.options as WhatsappDestination[];
export const WHATSAPP_DESTINATION_DEFAULT: WhatsappDestination =
  rateTable.destination.default as WhatsappDestination;

/** Coerce anything to one of the two legal sides. */
export function asWhatsappDestination(raw: unknown): WhatsappDestination {
  const v = String(raw ?? "");
  return (WHATSAPP_DESTINATIONS as readonly string[]).includes(v)
    ? (v as WhatsappDestination)
    : WHATSAPP_DESTINATION_DEFAULT;
}

/**
 * Seed the form from a `GET /api/settings` response. An absent row takes its
 * default — there is no migration, so most firms have none of the six until
 * the first save.
 */
export function readWhatsappRates(
  settings: Record<string, unknown> | null | undefined,
): WhatsappRateForm {
  const stored = settings || {};
  const form = {} as WhatsappRateForm;
  for (const field of WHATSAPP_RATE_FIELDS) {
    const raw = stored[WHATSAPP_RATE_KEYS[field]];
    const value =
      raw == null || raw === "" || !Number.isFinite(Number(raw))
        ? WHATSAPP_RATE_DEFAULTS[field]
        : Number(raw);
    form[field] = String(value);
  }
  return form;
}

/**
 * The `PUT /api/settings` body. Numbers, not strings — the server coerces
 * defensively but a panel that stores "15" and a panel that stores 15 would
 * make the same Setting row read two ways.
 *
 * A field left empty or unparseable saves its DEFAULT rather than zero: zero
 * minutes per block is a real setting somebody might want, and it must not be
 * reachable by clearing a box and pressing Save.
 */
export function whatsappRatePayload(
  form: WhatsappRateForm,
): Record<string, number> {
  const payload: Record<string, number> = {};
  for (const field of WHATSAPP_RATE_FIELDS) {
    const n = Number(form[field]);
    payload[WHATSAPP_RATE_KEYS[field]] = Number.isFinite(n) && form[field] !== ""
      ? n
      : WHATSAPP_RATE_DEFAULTS[field];
  }
  return payload;
}
